import SEO from '../components/SEO';
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { services } from '../mock';
import * as LucideIcons from 'lucide-react';
import EstimationBanner from '../components/EstimationBanner';

const Services = () => {
  // Service images mapping with user-provided photos
  const serviceImages = {
    'maison': '/images/maison.jpg',
    'appartement': '/images/appartement.jpg',
    'cave-grenier': '/images/garage.jpg',
    'professionnel': '/images/professionnel.jpg',
    'succession': '/images/succession.jpg'
  };

  const serviceLinks = {
    'maison': '/services/maison',
    'appartement': '/services/appartement',
    'cave-grenier': '/services/cave-grenier',
    'professionnel': '/services/professionnel',
    'succession': '/services/succession'
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Services de débarras professionnel | DEBECO Annecy"
        description="Découvrez nos services de débarras : maison, appartement, cave, grenier, succession, locaux professionnels. Intervention rapide en Savoie et Haute-Savoie."
        canonical="/services"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {"@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://debeco.fr/"},
            {"@type": "ListItem", "position": 2, "name": "Services"}
          ]
        }}
      />
      {/* Hero - Harmonized */}
      <section className="hero-banner text-white py-16">
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Nos services de débarras</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Des solutions complètes et professionnelles pour tous vos besoins de débarras
          </p>
        </div>
      </section>

      {/* Services Details */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="space-y-16">
            {services.map((service, index) => {
              const IconComponent = LucideIcons[service.icon];
              const isEven = index % 2 === 0;
              const imageUrl = serviceImages[service.id] || service.image;
              const linkUrl = serviceLinks[service.id] || '/services';
              
              return (
                <div key={service.id} className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-center ${!isEven ? 'lg:flex-row-reverse' : ''}`}>
                  {/* Image */}
                  <div className={`${!isEven ? 'lg:order-2' : ''}`}>
                    <div className="relative h-80 rounded-2xl overflow-hidden shadow-2xl group">
                      <img 
                        src={imageUrl} 
                        alt={service.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                        width="600"
                        height="320"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-blue-900/80 to-transparent flex items-end p-6">
                        {IconComponent && (
                          <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center">
                            <IconComponent className="w-10 h-10 text-blue-900" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className={`${!isEven ? 'lg:order-1' : ''}`}>
                    <h2 className="text-3xl font-bold text-blue-900 mb-4">{service.title}</h2>
                    <p className="text-gray-700 mb-6 text-lg leading-relaxed">{service.description}</p>
                    
                    <ul className="space-y-3 mb-6">
                      {service.details.map((detail, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{detail}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="flex flex-col sm:flex-row gap-3">
                      <Link to={linkUrl}>
                        <Button className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white shadow-lg">
                          En savoir plus
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                      </Link>
                      <Link to="/devis">
                        <Button variant="outline" className="border-2 border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white">
                          Estimer le coût
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Estimation Banner */}
      <EstimationBanner />

      {/* Recyclage Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          {/* Recyclage Info */}
          <div className="max-w-4xl mx-auto">
            <Card className="border-2 border-emerald-500 bg-gradient-to-br from-emerald-50 to-green-50">
              <CardContent className="p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-blue-900 mb-3">Recyclage inclus dans tous nos services</h3>
                    <p className="text-gray-700 mb-4 leading-relaxed">
                      Quel que soit le type de débarras que vous choisissez, nous appliquons systématiquement une démarche écologique et responsable. Notre engagement envers l'environnement fait partie intégrante de chacune de nos interventions.
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700"><strong>Tri sélectif systématique</strong> de tous les objets et matériaux</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700"><strong>Don aux associations locales</strong> (Emmaüs, Croix-Rouge) pour les objets en bon état</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700"><strong>Réutilisation des objets fonctionnels</strong> pour leur donner une seconde vie</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700"><strong>Traitement en déchetterie agréée</strong> dans le respect des normes</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">Comment ça marche ?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Un processus simple et efficace</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              { step: '1', title: 'Contact', desc: 'Appelez-nous ou remplissez le formulaire' },
              { step: '2', title: 'Devis gratuit', desc: 'Visite sur place et estimation' },
              { step: '3', title: 'Intervention', desc: 'Débarras complet sous 24-48h' }
            ].map((item) => (
              <Card key={item.step} className="text-center border-2 hover:border-emerald-500 hover:shadow-xl transition-all">
                <CardContent className="p-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-900 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <span className="text-2xl font-bold text-white">{item.step}</span>
                  </div>
                  <h3 className="font-bold text-xl text-blue-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-emerald-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Prêt à démarrer ?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Contactez-nous dès maintenant pour un devis gratuit et une intervention rapide
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100 shadow-xl text-lg px-8 py-6 w-full sm:w-auto">
                Demander un devis gratuit
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:0451401617">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 text-lg px-8 py-6 w-full sm:w-auto">
                <Phone className="mr-2 w-5 h-5" />
                04 51 40 16 17
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
